import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useErrorCatch from 'hooks/useErrorCatch';

import { SimpleOptionType } from 'types/filters';

import { SelectValue } from '../filter.types';
import { loadLocationOptions } from '../utils/loadLocationOptions';

export interface UseCountryStateCityProps {
  countryValue?: string;
  cityValue?: string;
  stateValue?: string;
  onChangeCountry: (value?: string) => void;
  onChangeState: (value?: string) => void;
  onChangeCity: (value?: string) => void;
  countries: { code: string; label: string }[];
}

export const useCountryStateCity = ({
  countryValue,
  cityValue,
  stateValue,
  onChangeCountry,
  onChangeState,
  onChangeCity,
  countries
}: UseCountryStateCityProps) => {
  const { t } = useTranslation();
  const [states, setStates] = useState<SimpleOptionType[]>([]);
  const [cities, setCities] = useState<SimpleOptionType[]>([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const catchError = useErrorCatch();
  const loadedCountryRef = useRef<string | undefined>();
  const statesRef = useRef<SimpleOptionType[]>([]);

  const countriesOptions: SimpleOptionType[] = countries.map(
    ({ code, label }) => ({
      label,
      value: code,
      key: code
    })
  );

  const handleCountryChange = (value: SelectValue) => {
    const country = value as string;
    onChangeCountry(country);
    onChangeState(undefined);
    onChangeCity(undefined);
    statesRef.current = [];
    setStates([]);
    setCities([]);
  };

  const handleStateChange = (value: SelectValue) => {
    const state = value as string;
    onChangeState(state);
    onChangeCity(undefined);
    setCities([]);
  };

  const handleCityChange = (value: SelectValue) => {
    onChangeCity(value as string);
  };

  useEffect(() => {
    if (!countryValue) {
      loadedCountryRef.current = undefined;
      statesRef.current = [];
      setStates([]);
      setCities([]);
      return;
    }

    let cancelled = false;

    loadLocationOptions({
      countryValue,
      stateValue,
      shouldFetchStates: loadedCountryRef.current !== countryValue,
      isCancelled: () => cancelled,
      statesRef,
      loadedCountryRef,
      setStates,
      setCities,
      setIsLoadingStates,
      setIsLoadingCities,
      catchError,
      errorMessages: {
        fetchStates: t('error.message.fetchStates'),
        fetchCities: t('error.message.fetchCities')
      }
    });

    return () => {
      cancelled = true;
    };
  }, [countryValue, stateValue, catchError, t]);

  const hasStates = states.length > 0;
  const hasCities = cities.length > 0;
  const statesLoaded = !isLoadingStates && !!countryValue;

  const isShowState = !!countryValue && statesLoaded && hasStates;

  const isShowCity =
    !!countryValue && statesLoaded && (!hasStates || !!stateValue) && hasCities;

  return {
    country: countryValue,
    state: stateValue,
    city: cityValue,
    states,
    cities,
    countriesOptions,
    handleCountryChange,
    handleStateChange,
    handleCityChange,
    isShowState,
    isShowCity,
    hasStates,
    isLoadingStates,
    isLoadingCities
  };
};
